import { LitElement, TemplateResult } from 'lit';
import { customElement, query, queryAll, property } from 'lit/decorators.js';

import { ImageTemplateArgs } from '@components/image/image.template';
import Swiper from 'swiper';
import { Keyboard, Navigation, Pagination, Autoplay, EffectFade } from 'swiper/modules';
import { ButtonTemplateArgs } from '@components/button/button.template';
import { BREAKPOINTS } from '@constants/breakpoints.ts';
import { throttle } from '@kluntje/js-utils/lib/function-helpers/index.js';

export interface StageArgs {
  autopauseDisabled?: boolean;
  autoplay?: boolean;
  delay?: number;
  extraButtons?: boolean;
  items: StageItemArgs[];
  navigationDropdown?: boolean;
  navigationEnabled?: boolean;
  navigationItems?: StageNavItemArgs[];
  navigationTopUsedMarkup?: TemplateResult;
  parallax: boolean;
}

export interface StageItemArgs {
  withVideo?: boolean;
  image?: ImageTemplateArgs;
  title: string;
  subline?: string;
  buttonTemplateArgs?: ButtonTemplateArgs;
  gradient?: '0' | '30' | '50' | '80';
}

export interface StageNavItemArgs {
  icon?: string;
  title: string;
  link: string;
}

export interface StageNavTemplate {
  navigationDropdown?: boolean;
  navigationItems?: StageNavItemArgs[];
  extraButtons?: boolean;
}

@customElement('man-stage')
export class ManStage extends LitElement {
  @queryAll('.man-stage-item')
  private items!: NodeListOf<HTMLElement>;

  @queryAll('.man-stage-item__background')
  private parallaxEl!: NodeListOf<HTMLElement>;

  @query('.slider')
  private slider!: HTMLElement;

  @query('.swiper-button-next')
  private swiperButtonNext!: HTMLElement;

  @query('.swiper-button-prev')
  private swiperButtonPrev!: HTMLElement;

  @query('.swiper-pagination')
  private swiperPagination!: HTMLElement;

  @query('.man-stage-nav')
  private stageNav!: HTMLElement;

  @query('.man-stage-nav__wrapper')
  private stageNavWrapper!: HTMLElement;

  @query('.man-stage-nav__content')
  private stageNavContent!: HTMLElement;

  private showClass: string = 'active';

  private stageNavMinWidth: number = 0;

  @property({ attribute: 'autoplay' })
  autoplay = 'false';

  @property({ attribute: 'delay', type: Number })
  delay = 5000;

  @property({ attribute: 'autopause-disabled' })
  autopauseDisabled = 'false';

  @property({ attribute: 'parallax', type: Boolean })
  parallax = false;

  protected createRenderRoot(): HTMLElement | DocumentFragment {
    return this;
  }

  protected initSwiper = () => {
    const autoplayObj =
      this.autoplay === 'true'
        ? {
            autoplay: {
              delay: this.delay || 5000,
              pauseOnMouseEnter: !(this.autopauseDisabled === 'true'),
            },
          }
        : { autoplay: false };

    new Swiper(this.slider, {
      modules: [Keyboard, Navigation, Pagination, Autoplay, EffectFade],
      keyboard: {
        enabled: true,
        onlyInViewport: true,
      },
      navigation: {
        nextEl: this.swiperButtonNext,
        prevEl: this.swiperButtonPrev,
      },
      pagination: {
        el: this.swiperPagination,
        clickable: true,
      },
      slidesPerView: 1,
      loop: true,
      effect: 'fade',
      ...autoplayObj,
    });
  };

  protected initParallax = () => {
    const observer = new IntersectionObserver(this.onIntersect, {});
    observer.observe(this);
  };

  protected onIntersect = (entries: IntersectionObserverEntry[]) => {
    entries.forEach((entry: IntersectionObserverEntry) => {
      if (entry.target === this && entry.intersectionRatio) {
        document.addEventListener('scroll', this.onScroll);
      } else {
        document.removeEventListener('scroll', this.onScroll);
      }
    });
  };

  protected onClickOutsideMenu = (event: MouseEvent): void => {
    const target = event.target as HTMLElement;

    if (!this.stageNavWrapper?.contains(target)) {
      this.stageNavWrapper.classList.remove(this.showClass);
      document.removeEventListener('click', this.onClickOutsideMenu);
    }

    this.stageNavContent?.classList.remove(this.showClass);
  };

  protected onStageNavDropdownMenuClick = () => {
    this.stageNavWrapper.addEventListener('click', () => {
      this.stageNavWrapper.classList.toggle(this.showClass);
      this.stageNavWrapper.scrollIntoView({ behavior: 'smooth', block: 'start' });

      if (this.stageNavWrapper.classList.contains(this.showClass)) {
        document.addEventListener('click', this.onClickOutsideMenu);
      } else {
        document.removeEventListener('click', this.onClickOutsideMenu);
      }
    });
  };

  protected getScrollPosition(): number {
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  protected setStageNavMinWidth = () => {
    // remove full width to get nav min-width
    this.stageNav?.classList.remove('man-stage-nav--full-width');

    this.stageNavMinWidth = this.stageNav?.offsetWidth;
    this.stageNavContent.style.minWidth =
      Math.min(100, (this.stageNavMinWidth * 100) / document.body.clientWidth) + '%';

    // make stage nav full width after navigation min-width was set
    this.stageNav?.classList.add('man-stage-nav--full-width');
  };

  protected initStageNav = () => {
    // to make space for buttons and man-stage__content
    this?.classList.add('man-stage--with-nav');

    this.onStageNavDropdownMenuClick();

    this.setStageNavMinWidth();

    window.addEventListener(
      'resize',
      throttle(() => {
        this.setStageNavMinWidth();

        this.stageNavOnScroll();
      }, 500)
    );
  };

  protected stageNavOnScroll = () => {
    const windowWidth = document.documentElement.clientWidth;
    const windowHeight = document.documentElement.clientHeight;
    const spaceForContent = windowWidth - this.stageNavMinWidth;
    const spaceForContentPercent = (spaceForContent * 100) / windowWidth;
    const contentWidthPercentage = (this.stageNavMinWidth * 100) / windowWidth;
    const stageNavToTopGap = windowHeight - this.getScrollPosition() - this.stageNavContent?.clientHeight;
    const scrollFromTopPercent = (stageNavToTopGap * 100) / (windowHeight - this.stageNavContent?.clientHeight);
    const scrollPercentage = contentWidthPercentage + (spaceForContentPercent * (100 - scrollFromTopPercent)) / 100;

    // stageNav is sticky
    this.stageNav?.classList.toggle('man-stage-nav--sticky', stageNavToTopGap < 0);

    // set width for animation
    if (this.stageNavContent) {
      this.stageNavContent.style.width =
        windowWidth > BREAKPOINTS.xm.max ? Math.min(100, scrollPercentage) + '%' : '100%';
    }
  };

  protected onScroll = () => {
    if (this.stageNavContent) {
      this.stageNavOnScroll();
    }

    if (this.parallax) {
      const translateValue = Math.floor(window.scrollY * 0.5);

      [...this.parallaxEl].forEach((el) => {
        el.style.transform = `translateY(${translateValue}px)`;
      });
    }
  };

  initHeaderObserver = () => {
    const header = document.querySelector('header');
    if (!header) {
      return;
    }

    const config = { attributes: true };

    const onHeaderMutate = (mutationList: MutationRecord[]) => {
      for (const mutation of mutationList) {
        if (mutation.type === 'attributes') {
          this.classList.toggle('man-stage--desktop-header', header.classList.contains('header--desktop'));
        }
      }
    };

    const observer = new MutationObserver(onHeaderMutate);
    observer.observe(header, config);
  };

  protected firstUpdated(): void {
    if (this.stageNav) {
      this.initStageNav();
    }

    if (this.items.length > 1) {
      this.initSwiper();
    }

    if (this.parallax) {
      this.initParallax();
    }

    this.initHeaderObserver();

    this.onScroll();
    this.stageNavOnScroll();
  }
}
